import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import type { BlueprintDocument, SecretChangePolicy, SecretDeclaration } from '../api/types';
import { declarationInputId, type DeclarationsEditing } from './declarations';
import { IconButton } from '../components/ui';
import type { MappedDiagnostic } from '../lib/diagnostics';
import { GENERATE_MAX_LENGTH, GENERATE_MIN_LENGTH, MAX_SECRETS, secretNameProblem } from '../lib/secrets';
import { strings } from '../strings';
import type { VariableRow } from './model';

const d = strings.declarations;

function NameInput({
  name,
  names,
  readOnly,
  onRename,
}: {
  name: string;
  names: readonly string[];
  readOnly: boolean;
  onRename: (next: string) => void;
}) {
  const [draft, setDraft] = useState(name);
  const [error, setError] = useState<string | null>(null);
  const id = declarationInputId(name, 'name');
  const commit = () => {
    if (draft === name) return setError(null);
    const problem = secretNameProblem(draft, names, name);
    if (problem) return setError(d.nameProblem[problem]);
    setError(null);
    onRename(draft);
  };
  return (
    <div className="min-w-0">
      <input
        id={id}
        className="input font-mono"
        aria-label={`${d.name}: ${name}`}
        value={draft}
        readOnly={readOnly}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          } else if (event.key === 'Escape') {
            setDraft(name);
            setError(null);
          }
        }}
      />
      {error && (
        <p id={`${id}-error`} className="field-error">
          {error}
        </p>
      )}
    </div>
  );
}

function DeclarationRow({
  name,
  declaration,
  names,
  users,
  diagnostics,
  editing,
  helpId,
}: {
  name: string;
  declaration: SecretDeclaration;
  names: readonly string[];
  users: readonly string[];
  diagnostics: readonly MappedDiagnostic[];
  editing: DeclarationsEditing | null;
  /** The editor's explanation of Generate (one for all rows). */
  helpId: string;
}) {
  const readOnly = !editing;
  const id = (field: string) => declarationInputId(name, field);
  const length = declaration.generate?.length;
  const change = (next: SecretDeclaration, field: string) =>
    editing?.change(name, next, `secret:${name}:${field}`);
  return (
    <tr data-testid={`declaration-${name}`} className="var-row align-top">
      <td className="var-td">
        <NameInput
          key={name}
          name={name}
          names={names}
          readOnly={readOnly}
          onRename={(next) => editing?.rename(name, next)}
        />
        {diagnostics.length > 0 && (
          <ul className="mt-1 space-y-0.5 text-[12px]">
            {diagnostics.map(({ diagnostic }, i) => (
              <li key={i} className={diagnostic.severity === 'error' ? 'text-danger' : 'text-warning'}>
                <span className="font-mono">{diagnostic.code}</span> {diagnostic.message}
              </li>
            ))}
          </ul>
        )}
      </td>
      <td className="var-td">
        <input
          id={id('description')}
          className="input"
          aria-label={`${d.description}: ${name}`}
          maxLength={500}
          readOnly={readOnly}
          value={declaration.description ?? ''}
          onChange={(event) =>
            change({ ...declaration, description: event.target.value || undefined }, 'description')
          }
        />
      </td>
      <td className="var-td">
        <select
          id={id('onChange')}
          className="input"
          aria-label={`${d.onChange}: ${name}`}
          disabled={readOnly}
          value={declaration.onChange ?? 'wait'}
          onChange={(event) =>
            change({ ...declaration, onChange: event.target.value as SecretChangePolicy }, 'onChange')
          }
        >
          <option value="wait">{d.wait}</option>
          <option value="restart">{d.restart}</option>
        </select>
      </td>
      <td className="var-td">
        <span className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5 text-[13px]">
            <input
              id={id('generate')}
              type="checkbox"
              disabled={readOnly}
              checked={length !== undefined}
              aria-describedby={helpId}
              onChange={(event) =>
                change(
                  { ...declaration, generate: event.target.checked ? { length: 32 } : undefined },
                  'generate',
                )
              }
            />
            {d.generate}
          </label>
          {length !== undefined && (
            <input
              id={id('length')}
              type="number"
              className="input w-20"
              aria-label={`${d.length}: ${name}`}
              aria-describedby={helpId}
              min={GENERATE_MIN_LENGTH}
              max={GENERATE_MAX_LENGTH}
              readOnly={readOnly}
              value={length}
              onChange={(event) => {
                const next = Number(event.target.value);
                if (Number.isInteger(next)) change({ ...declaration, generate: { length: next } }, 'length');
              }}
            />
          )}
        </span>
      </td>
      <td className="var-td font-mono text-[12px]">
        {users.length ? users.join(', ') : <span className="font-sans text-fg-muted">{d.usage(0)}</span>}
      </td>
      <td className="var-td var-col-actions">
        {editing && (
          <IconButton label={d.remove(name)} onClick={() => editing.remove(name)}>
            <Trash2 aria-hidden className="size-4" />
          </IconButton>
        )}
      </td>
    </tr>
  );
}

/**
 * The blueprint's secret declarations in the Variables window's Secrets view (spec 6.10.13), which replaced the
 * inspector's declarations editor. Read-only without `editing` (readers, read-only versions).
 */
export function DeclarationsEditor({
  doc,
  designRows,
  diagnostics,
  editing,
  focus,
}: {
  doc: BlueprintDocument;
  designRows: readonly VariableRow[];
  diagnostics?: ReadonlyMap<string, MappedDiagnostic[]>;
  editing: DeclarationsEditing | null;
  /** A declaration whose name field gets focus (a problem was chosen, or one was added). */
  focus?: { name: string; nonce: number } | null;
}) {
  const declarations = Object.entries(doc.secrets ?? {});
  const names = declarations.map(([name]) => name);
  const helpId = useId();
  const [added, setAdded] = useState<{ name: string; nonce: number } | null>(null);
  const target = added && (!focus || added.nonce > focus.nonce) ? added : focus;
  useEffect(() => {
    if (!target) return;
    let frames = 30;
    const tick = () => {
      const input = document.getElementById(
        declarationInputId(target.name, 'name'),
      ) as HTMLInputElement | null;
      if (input) {
        input.focus();
        input.select();
      } else if (frames-- > 0) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [target]);

  return (
    <div className="min-h-0 flex-1 overflow-auto" data-testid="declarations">
      <div className="var-vault-strip">
        <span className="font-semibold">{d.tableLabel}</span>
        <span id={helpId} className="help">
          {d.help} {d.generateHelp} {d.length}: {d.lengthHelp}
        </span>
        {editing && names.length < MAX_SECRETS && (
          <button
            type="button"
            className="btn btn-sm ml-auto"
            onClick={() => setAdded({ name: editing.add(), nonce: Date.now() })}
          >
            <Plus aria-hidden className="size-4" />
            {d.add}
          </button>
        )}
      </div>
      {declarations.length === 0 ? (
        <p className="var-empty">{strings.variables.empty.noDeclarations}</p>
      ) : (
        <table className="table var-table" aria-label={d.tableLabel}>
          <thead>
            <tr>
              <th scope="col" className="var-th">
                {d.name}
              </th>
              <th scope="col" className="var-th">
                {d.description}
              </th>
              <th scope="col" className="var-th">
                {d.onChange}
              </th>
              <th scope="col" className="var-th">
                {d.generate}
              </th>
              <th scope="col" className="var-th">
                {d.usedBy}
              </th>
              <th scope="col" className="var-th">
                <span className="sr-only">{strings.variables.columns.actions}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {declarations.map(([name, declaration]) => (
              <DeclarationRow
                key={name}
                name={name}
                declaration={declaration}
                names={names}
                users={designRows
                  .filter((r) => r.secretName?.toLowerCase() === name.toLowerCase())
                  .map((r) => `${r.resourceId}.${r.displayName}`)}
                diagnostics={diagnostics?.get(name) ?? []}
                editing={editing}
                helpId={helpId}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
